import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'
import { SERVICE_URLS, type ServiceName, RTK_CACHE_LIFETIME_S } from '../../lib/config'

// ─────────────────────────────────────────────────────────────
// Service-aware base query
//
// RTK Query endpoints can tag their request with a `service` key
// in extraOptions to route to a specific microservice URL.
// Falls back to the monolith URL when no service is specified.
//
// Usage in an endpoint:
//   query: (id) => ({ url: `/discussions/${id}` }),
//   extraOptions: { service: 'discussions' },
// ─────────────────────────────────────────────────────────────

function makeBaseQuery(service: ServiceName) {
  return fetchBaseQuery({
    baseUrl: SERVICE_URLS[service],
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken
      if (token) headers.set('Authorization', `Bearer ${token}`)
      return headers
    },
  })
}

// Cache one fetchBaseQuery per service URL to avoid recreating them
const queryCache = new Map<string, ReturnType<typeof fetchBaseQuery>>()

function getBaseQuery(service: ServiceName) {
  const url = SERVICE_URLS[service]
  if (!queryCache.has(url)) queryCache.set(url, makeBaseQuery(service))
  return queryCache.get(url)!
}

// RTK Query does NOT do its own refresh — App.tsx owns the refresh cycle.
// This prevents race conditions where two simultaneous refresh calls
// trigger breach detection and log the user out.
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { service?: ServiceName }
> = async (args, api, extraOptions) => {
  // Route to service-specific URL if specified, otherwise use monolith
  const service: ServiceName = extraOptions?.service ?? 'discussions'
  const baseQuery = getBaseQuery(service)
  const result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    const errorCode = (result.error.data as { code?: string } | undefined)?.code
    // Logout on expired or invalid token — background refresh should prevent
    // TOKEN_EXPIRED in normal use, so hitting this means the session is truly dead
    if (errorCode === 'TOKEN_INVALID' || errorCode === 'TOKEN_EXPIRED') {
      api.dispatch({ type: 'auth/logout' })
    }
  }

  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: RTK_CACHE_LIFETIME_S,
  tagTypes: [
    'Discussion',
    'Comment',
    'User',
    'Category',
    'Notification',
    'Agent',
    'Plan',
    'Subscription',
    'AIRequest',
    'Report',
    'Poll',
    'ChatAgent',
    'ChatConversation',
    'ChatMessage',
    'Developer',
    'Knowledge',
    'Payout',
    'LegalDocument',
    'AgentSubscription',
  ],
  endpoints: () => ({}),
})
